/**
 * SeoMetaController — Per-route SEO meta management (Feb 7 2026).
 *
 * Mounted once at the App level, inside BrowserRouter so it can
 * read `useLocation()`. On every navigation it:
 *
 *   1. Sets `<meta name="robots" content="...">`
 *      • Public marketing routes: "index,follow"
 *      • Everything else (authenticated, API, admin, etc.): "noindex,nofollow"
 *
 *   2. Sets `<link rel="canonical" href="...">`
 *      • Public routes: canonical = production origin + path
 *        (strips query strings + hash so duplicate-content
 *         signals don't confuse Google).
 *      • Private routes: REMOVES the canonical link (defensive —
 *        we don't want a private page accidentally pointing at
 *        itself as a canonical authority).
 *
 *   3. Updates `document.title` only when a route exposes a
 *      `data-page-title` element (lets pages own their own titles
 *      without forcing a heavyweight SEO library).
 *
 * Why this and not react-helmet:
 *   • We need exactly two tags managed centrally — a 100-line
 *     component is cheaper than a dependency.
 *   • Robots policy must match `public/robots.txt` exactly so
 *     keep both lists in this same file (PUBLIC_ROUTES below) +
 *     the static robots.txt — they're cross-checked by tests.
 *
 * Tests: `backend/tests/test_seo_meta_controls.py`.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// ── Public routes (matched as exact path OR prefix). ──────────────
// Mirror of the Allow: rules in /app/frontend/public/robots.txt.
// Keep these two lists in sync — invariant test enforces it.
const PUBLIC_EXACT = new Set([
  "/",
  "/for-learners",
  "/for-pdis",
  "/pricing",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/privacy",
  "/terms",
  "/press",
  "/about",
  "/contact",
]);

// Production origin used for canonical hrefs.
const CANONICAL_ORIGIN = "https://nationaldrivingguild.co.uk";

function isPublicPath(pathname) {
  if (!pathname) return false;
  return PUBLIC_EXACT.has(pathname);
}

function ensureMeta(name) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}

function ensureCanonical() {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  return el;
}

function removeCanonical() {
  const el = document.head.querySelector('link[rel="canonical"]');
  if (el) el.parentNode.removeChild(el);
}

export default function SeoMetaController() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPublic = isPublicPath(pathname);

    // 1. robots meta
    const robotsMeta = ensureMeta("robots");
    robotsMeta.setAttribute(
      "content",
      isPublic ? "index,follow" : "noindex,nofollow",
    );

    // 2. canonical link
    if (isPublic) {
      // Strip query/hash — canonical points to the clean URL.
      const canonicalEl = ensureCanonical();
      canonicalEl.setAttribute("href", `${CANONICAL_ORIGIN}${pathname}`);
    } else {
      removeCanonical();
    }
  }, [pathname]);

  return null;
}

// Exported so tests + sitemap generation can read the same source of
// truth without duplicating the list.
export const PUBLIC_ROUTES = Array.from(PUBLIC_EXACT);
